import { trackGearEvent as trackEvent } from './gear-analytics.js';

const NAVIGATION_KEY = 'sfpdcalls:editorial-navigation';
const allowedDestination = path => path === '/' || path === '/prod' || /^\/blog(?:\/[a-z0-9-]+)?$/.test(path);

function consumeNavigation() {
  try {
    const record = JSON.parse(window.sessionStorage.getItem(NAVIGATION_KEY));
    window.sessionStorage.removeItem(NAVIGATION_KEY);
    if (!record) return;
    const age = Date.now() - record.created_at;
    const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
    if (!allowedDestination(record.destination_path) || currentPath !== record.destination_path || !Number.isFinite(age) || age < 0 || age > 300000) return;
    trackEvent('editorial_navigation', { ...record.parameters, navigation_stage: 'arrival' });
  } catch { /* Restricted storage must not prevent navigation. */ }
}

function recordNavigation(event, link, info) {
  const destination = new URL(link.href).pathname.replace(/\/$/, '') || '/';
  if (!allowedDestination(destination)) return;
  const parameters = { ...info, page_path: window.location.pathname, destination_path: destination };
  const sameTab = event.type === 'click' && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && !event.defaultPrevented;
  if (sameTab) {
    try {
      window.sessionStorage.setItem(NAVIGATION_KEY, JSON.stringify({ destination_path: destination, parameters, created_at: Date.now() }));
      return;
    } catch { /* Blocked storage: fall back to the existing queue. */ }
  }
  trackEvent('editorial_navigation', { ...parameters, navigation_stage: 'activation' });
}

// Reuse the existing blocker-safe Google tag queue. Automatic pageviews,
// acquisition, engagement and outbound clicks stay with the existing GA4 tag.
export function installEditorial(root = document) {
  consumeNavigation();
  const article = root.querySelector('article[data-article-slug]');
  const info = article ? {
    article_slug: article.dataset.articleSlug,
    article_title: article.dataset.articleTitle,
    article_category: article.dataset.articleCategory,
  } : {};
  const canonical = root.querySelector('link[rel="canonical"]')?.href;
  const shareURL = article?.dataset.articleShareUrl || canonical;
  if (article) trackEvent('article_view', info);

  const copy = root.querySelector('[data-share="copy"]');
  if (copy && navigator.clipboard?.writeText) copy.hidden = false;
  const native = root.querySelector('[data-share="native"]');
  if (native && navigator.share) native.hidden = false;
  const status = root.querySelector('.share-status');
  const reportShare = method => trackEvent('article_share', { article_slug: info.article_slug, share_method: method });

  const frame = root.querySelector('[data-video-id]');
  const player = frame?.querySelector('iframe');
  let playRecorded = false;
  const postToPlayer = message => {
    try { player?.contentWindow?.postMessage(JSON.stringify(message), 'https://www.youtube-nocookie.com'); } catch { /* Player controls remain available. */ }
  };
  const listenForState = () => {
    postToPlayer({ event: 'listening', id: 'sfpdcalls-video' });
    postToPlayer({ event: 'command', func: 'addEventListener', args: ['onStateChange'], id: 'sfpdcalls-video' });
  };
  if (player) {
    // Keep the native YouTube player visible. Playback starts muted only when
    // the video enters the viewport; its own controls always remain available.
    const url = new URL(player.src);
    url.searchParams.set('origin', window.location.origin);
    player.addEventListener('load', listenForState);
    player.src = url.href;
    trackEvent('video_load', { article_slug: info.article_slug, video_provider: 'youtube', video_id: frame.dataset.videoId });
    if (typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.25)) return;
        observer.disconnect();
        if (playRecorded) return;
        const autoplayURL = new URL(player.src);
        autoplayURL.searchParams.set('autoplay', '1');
        autoplayURL.searchParams.set('mute', '1');
        player.src = autoplayURL.href;
      }, { threshold: 0.25 });
      observer.observe(frame);
    }
  }

  // Listen to the one player's messages; do not download the YouTube IFrame API.
  // Loading the visible iframe is never falsely reported as actual playback.
  window.addEventListener('message', event => {
    if (!player || event.source !== player.contentWindow || event.origin !== 'https://www.youtube-nocookie.com') return;
    let data;
    try { data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; } catch { return; }
    if (!data || typeof data !== 'object') return;
    if (data.event === 'onReady') listenForState();
    if (!playRecorded && ((data.event === 'onStateChange' && data.info === 1) || (data.event === 'infoDelivery' && data.info?.playerState === 1))) {
      playRecorded = true;
      trackEvent('video_play', { article_slug: info.article_slug, video_provider: 'youtube', video_id: frame.dataset.videoId });
    }
  });

  const navigation = event => {
    if (event.type === 'auxclick' && event.button !== 1) return;
    const link = event.target.closest?.('a[data-editorial-navigation]');
    if (link) recordNavigation(event, link, info);
  };
  root.addEventListener('auxclick', navigation);
  root.addEventListener('click', async event => {
    navigation(event);
    const share = event.target.closest?.('[data-share]');
    if (!share || !canonical) return;
    const method = share.dataset.share;
    if (method === 'copy') {
      try {
        await navigator.clipboard.writeText(shareURL);
        status.textContent = 'Link copied.';
        reportShare('copy');
      } catch { status.textContent = `Copy this link: ${shareURL}`; }
    } else if (method === 'native') {
      try { await navigator.share({ title: info.article_title, url: shareURL }); reportShare('native'); }
      catch (error) { if (error.name !== 'AbortError') status.textContent = `Share this link: ${shareURL}`; }
    } else reportShare(method);
  });
}

installEditorial();
